import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SavedFilter {
  readonly reportFilterId: string;
  readonly filterName: string;
  readonly isDefault: boolean;
}

interface ReportDefinitionDetail {
  readonly reportDefinitionId: string;
  readonly reportCode: string;
  readonly reportName: string;
  readonly description: string;
  readonly outputFormat: string;
  readonly status: string;
  readonly parameters: ReadonlyArray<{ readonly key: string; readonly label: string; readonly dataType: string }>;
}

interface ExecutionResult {
  readonly executionId: string;
  readonly executionStatus: string;
  readonly rowCount?: number;
}

/** L16.02 — Report runner (`/reports/runner/:reportId`). Shows the definition
 *  with its parameters and saved row-level filters, and lets the operator
 *  enter parameter values and run the report via `POST /generate` (M1). */
export function M16ReportRunnerPage(): ReactNode {
  const { t } = useTranslation();
  const { reportId } = useParams<{ reportId: string }>();
  const { token } = useAuth();
  const [def, setDef] = useState<ReportDefinitionDetail | null>(null);
  const [filters, setFilters] = useState<SavedFilter[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [execution, setExecution] = useState<ExecutionResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      apiRequest<ReportDefinitionDetail>(`/v1/m16/reports/definitions/${reportId}`, { token }),
      apiRequest<SavedFilter[]>(`/v1/m16/reports/filters/${reportId}`, { token }),
    ])
      .then(([d, f]) => { if (alive) { setDef(d); setFilters(f); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [reportId, token]);

  async function runReport(): Promise<void> {
    setRunning(true);
    setRunError(null);
    setExecution(null);
    try {
      const res = await apiRequest<ExecutionResult>('/v1/m16/reports/generate', {
        method: 'POST',
        token,
        body: { reportDefinitionId: reportId, filterConfig: paramValues },
      });
      setExecution(res);
    } catch (e) {
      setRunError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!def || !filters) return <p>{t('m16.runner.loading')}</p>;
  return (
    <main aria-label="report-runner">
      <h1>{t('m16.runner.title', { name: def.reportName })}</h1>
      <p>{def.description}</p>
      <h2>{t('m16.runner.parameters')}</h2>
      <table aria-label="report-parameters-table">
        <thead><tr><th>{t('m16.runner.colKey')}</th><th>{t('m16.runner.colLabel')}</th><th>{t('m16.runner.colType')}</th><th>{t('m16.runner.colValue')}</th></tr></thead>
        <tbody>
          {def.parameters.map((p) => (
            <tr key={p.key}>
              <td>{p.key}</td><td>{p.label}</td><td>{p.dataType}</td>
              <td>
                <input
                  aria-label={`param-${p.key}`}
                  data-testid={`param-${p.key}`}
                  value={paramValues[p.key] ?? ''}
                  onChange={(e) => setParamValues((v) => ({ ...v, [p.key]: e.target.value }))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h2>{t('m16.runner.savedFilters')}</h2>
      <table aria-label="saved-filters-table">
        <thead><tr><th>{t('m16.runner.colName')}</th><th>{t('m16.runner.colDefault')}</th></tr></thead>
        <tbody>
          {filters.map((f) => (
            <tr key={f.reportFilterId}><td>{f.filterName}</td><td>{f.isDefault ? t('m16.runner.yes') : t('m16.runner.no')}</td></tr>
          ))}
        </tbody>
      </table>
      <button type="button" data-testid="run-report-btn" disabled={running} onClick={runReport}>
        {running ? t('m16.runner.running') : t('m16.runner.runBtn')}
      </button>
      {runError && <p role="alert">{runError}</p>}
      {execution && (
        <div aria-label="execution-result">
          <p>{t('m16.runner.executionLabel')} <strong>{execution.executionStatus}</strong></p>
          <Link to={`/reports/runs/${execution.executionId}`} aria-label="view-execution">{t('m16.runner.viewExecution')}</Link>
        </div>
      )}
    </main>
  );
}
